import Loader from "components/Loaders/Loader";
import AuditLogEntryMapper from "components/Mappers/AuditLogEntryMapper";
import AlertMessage from "components/Message/AlertMessage";
import { fetchHandleGetAuditLogs } from "helpers/Audit/AuditHelpers";
import { TokenContext } from "helpers/constants/Contexts";
import { AuditOperationCategories } from "helpers/enums/AuditOperationCategories";
import { IAuditLog } from "interfaces/Audit/IAuditLog";
import { useContext, useEffect, useMemo, useState } from "react";
import { Modal, Tab, Table, Tabs } from "react-bootstrap";
import { useDispatch } from "react-redux";

// Define interfaces for better type safety
interface IAuditError {
  show: boolean;
  message: string;
}

interface IAuditState {
  logs: IAuditLog[];
  loading: boolean;
  error: IAuditError;
}

interface IAuditTabContentProps {
  auditLogs: IAuditLog[];
  categories: AuditOperationCategories[];
  headers: string[];
  timezone: string;
}

// Create reusable tab content component
const AuditTabContent = ({
  auditLogs,
  categories,
  headers,
  timezone,
}: IAuditTabContentProps) => {
  return (
    <div className="overflow-x-auto bg-white rounded">
      <Table className="w-full">
        <thead className="bg-gray-100">
          <tr>
            {headers.map((header, index) => (
              <th key={index} className="p-3 text-left">
                {header.includes("Time") ? (
                  <>
                    {"Time "}
                    <span className="font-light text-sm">{`(${timezone})`}</span>
                  </>
                ) : (
                  header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <AuditLogEntryMapper
            auditLogs={auditLogs}
            operationCategories={categories}
          />
        </tbody>
      </Table>
    </div>
  );
};

const AccountAuditWindow = ({ show = false, handleClose, account }) => {
  const dispatch = useDispatch();
  const accessToken = useContext(TokenContext);
  const [auditState, setAuditState] = useState<IAuditState>({
    logs: [],
    loading: true,
    error: {
      show: false,
      message: "",
    },
  });

  const { logs, loading, error } = auditState;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Define tab configurations for better readability and maintainability
  const tabConfigs = useMemo(
    () => [
      {
        eventKey: "licence",
        title: "Licence",
        headers: [
          "Licence Type",
          "Expiry",
          "Changed by",
          "Date of change",
          "Time",
          "",
        ],
        categories: [AuditOperationCategories.LicenceChange],
      },
      {
        eventKey: "status",
        title: "Status",
        headers: ["Status", "Changed by", "Date of change", "Time", ""],
        categories: [AuditOperationCategories.StatusChange],
      },
      {
        eventKey: "details",
        title: "Details",
        headers: [
          "First name",
          "Last name",
          "Organisation",
          "Changed by",
          "Date of change",
          "Time",
          "",
        ],
        categories: [
          AuditOperationCategories.DetailsChange,
          AuditOperationCategories.OrganisationChange,
        ],
      },
    ],
    []
  );

  useEffect(() => {
    // Only fetch data when the modal is shown and we have an account ID
    if (show && account?.accountId) {
      setAuditState((prev) => ({ ...prev, loading: true }));

      dispatch(
        fetchHandleGetAuditLogs(
          accessToken,
          account.accountId,
          (logs) => {
            setAuditState({
              logs,
              loading: false,
              error: { show: false, message: "" },
            });
          },
          (message) => {
            setAuditState({
              logs: [],
              loading: false,
              error: { show: true, message },
            });
          }
        )
      );
    }
  }, [show, account?.accountId, accessToken, dispatch]);

  const renderAccountInfo = () => (
    <div className="bg-white rounded-md p-4 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <h4 className="font-bold text-xl mb-0">
          {`${account.firstName} ${account.lastName}`}
        </h4>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-gray-600">
          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
            {account.email}
          </span>
          {account.organisation?.organisationName && (
            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              {account.organisation.organisationName}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="xl"
      className="audit-window"
    >
      <Modal.Header
        className="bg-grey-dark-2 text-white border-b-grey-light-2"
        closeButton
        closeVariant="white"
      >
        <Modal.Title className="w-full text-center font-bold">
          Account History
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-5 bg-grey-light">
        {loading ? (
          <Loader maxHeight={300} />
        ) : error.show ? (
          <AlertMessage success={false} title="Error" message={error.message} />
        ) : (
          <div className="flex flex-col gap-4">
            {renderAccountInfo()}
            <Tabs defaultActiveKey="licence" variant="pills" data-color="black">
              {tabConfigs.map(({ eventKey, title, headers, categories }) => (
                <Tab key={eventKey} eventKey={eventKey} title={title}>
                  <AuditTabContent
                    auditLogs={logs}
                    categories={categories}
                    headers={headers}
                    timezone={timezone}
                  />
                </Tab>
              ))}
            </Tabs>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AccountAuditWindow;
